import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { supabase } from '@/lib/supabase';
import { eurosToCents, centsToEuros } from '@/lib/utils';
import type { Financing, FinancingFormData } from '@/types';

const financingSchema = z.object({
  loan_amount: z.number().min(1, 'Darlehenssumme erforderlich'),
  interest_rate: z.number().min(0).max(20),
  repayment_rate: z.number().min(0).max(20),
  start_date: z.string().min(1, 'Startdatum erforderlich'),
  fixed_interest_until: z.string().optional().nullable(),
  notes: z.string().optional().nullable(),
});

interface FinancingFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  propertyId: string;
  financing?: Financing | null;
  onSuccess: () => void;
}

export function FinancingFormModal({
  isOpen,
  onClose,
  propertyId,
  financing,
  onSuccess,
}: FinancingFormModalProps) {
  const isEdit = !!financing;

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FinancingFormData>({
    resolver: zodResolver(financingSchema),
  });

  useEffect(() => {
    if (financing) {
      reset({
        loan_amount: centsToEuros(financing.loan_amount) || 0,
        interest_rate: financing.interest_rate,
        repayment_rate: financing.repayment_rate,
        start_date: financing.start_date,
        fixed_interest_until: financing.fixed_interest_until || '',
        notes: financing.notes || '',
      });
    } else {
      reset({
        loan_amount: 0,
        interest_rate: 3.5,
        repayment_rate: 2.0,
        start_date: new Date().toISOString().split('T')[0],
        fixed_interest_until: '',
        notes: '',
      });
    }
  }, [financing, reset]);

  const onSubmit = async (data: FinancingFormData) => {
    try {
      const payload = {
        property_id: propertyId,
        loan_amount: eurosToCents(data.loan_amount),
        interest_rate: data.interest_rate,
        repayment_rate: data.repayment_rate,
        start_date: data.start_date,
        fixed_interest_until: data.fixed_interest_until || null,
        notes: data.notes || null,
        is_active: true,
      };

      if (isEdit && financing) {
        const { error } = await supabase
          .from('financings')
          .update(payload)
          .eq('id', financing.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('financings')
          .insert(payload);
        if (error) throw error;
      }

      onSuccess();
      onClose();
    } catch (error) {
      console.error('Financing save error:', error);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-background/80 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-md bg-card border border-border rounded-lg shadow-lg m-4">
        <div className="flex items-center justify-between p-4 border-b border-border">
          <h2 className="text-lg font-semibold">
            {isEdit ? 'Finanzierung bearbeiten' : 'Neue Finanzierung'}
          </h2>
          <button onClick={onClose} className="p-1 rounded-md hover:bg-accent">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="p-4 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="loan_amount">Darlehenssumme (€) *</Label>
              <Input
                id="loan_amount"
                type="number"
                step="0.01"
                {...register('loan_amount', { valueAsNumber: true })}
                error={errors.loan_amount?.message}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="interest_rate">Zinssatz (%)</Label>
                <Input
                  id="interest_rate"
                  type="number"
                  step="0.01"
                  {...register('interest_rate', { valueAsNumber: true })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="repayment_rate">Tilgung (%)</Label>
                <Input
                  id="repayment_rate"
                  type="number"
                  step="0.01"
                  {...register('repayment_rate', { valueAsNumber: true })}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="start_date">Startdatum *</Label>
                <Input
                  id="start_date"
                  type="date"
                  {...register('start_date')}
                  error={errors.start_date?.message}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="fixed_interest_until">Zinsbindung bis</Label>
                <Input
                  id="fixed_interest_until"
                  type="date"
                  {...register('fixed_interest_until')}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Notizen</Label>
              <textarea
                id="notes"
                rows={2}
                className="flex w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-ring resize-none"
                {...register('notes')}
              />
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 p-4 border-t border-border">
            <Button type="button" variant="secondary" onClick={onClose}>
              Abbrechen
            </Button>
            <Button type="submit" isLoading={isSubmitting}>
              {isEdit ? 'Speichern' : 'Hinzufügen'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}